'use client';

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useCookies } from 'next-client-cookies';
import { useMediaQueryReturn } from './types';

// EWM-760 — resize is a real external source, so it's a textbook
// useSyncExternalStore: subscribe to 'resize', read window.innerWidth as the
// client snapshot. Module-level + stable across renders (no deps), so
// useSyncExternalStore never needlessly resubscribes.
const subscribeToResize = (callback: () => void) => {
  window.addEventListener('resize', callback);
  return () => window.removeEventListener('resize', callback);
};
const getWidthSnapshot = () => window.innerWidth;

//custom hook that returns if current screen width is larger than
//the width breakpoint argument or not
const useMediaQuery = <T>(breakpoint?: T) => {
  const cookies = useCookies();

  // SSR/first-paint snapshot — the previously-persisted cookie value, same as
  // the old useState initializer.
  const getServerWidthSnapshot = useCallback(() => parseInt(cookies.get('width') ?? '0'), [cookies]);

  //current width — real window width on the client once hydrated, falls back
  //to the last-persisted cookie value on the server / first paint
  const width = useSyncExternalStore(subscribeToResize, getWidthSnapshot, getServerWidthSnapshot);

  // Persisting the width to a cookie is a side effect (not a derived state
  // value), so it stays in a plain effect — no setState here, only a write to
  // an external store (the cookie jar). Runs once on mount (mirrors the old
  // updateWidth() mount call) and again whenever width changes (mirrors the
  // old resize handler's cookie write).
  useEffect(() => {
    cookies.set('width', width.toString());
  }, [width, cookies]);

  //return a destructible array that includes the boolean and the width also
  return (breakpoint ? [width >= +breakpoint, width] : [width]) as useMediaQueryReturn<T>;
};

export default useMediaQuery;
